import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "preact/jsx-runtime";
import { useTranslation, Trans } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router';
import '@etchteam/diamond-ui/canvas/Card/Card';
import '@etchteam/diamond-ui/composition/Grid/Grid';
import '@etchteam/diamond-ui/composition/Grid/GridItem';
import '@etchteam/diamond-ui/control/Button/Button';
import '@/components/canvas/IconCircle/IconCircle';
import '@/components/composition/IconText/IconText';
import '@/components/composition/BorderedList/BorderedList';
import '@/components/control/IconLink/IconLink';
import '@/components/content/Icon/Icon';
import '@/components/content/Container/Container';
import SchemeContainerSummary from '@/components/template/SchemeContainerSummary/SchemeContainerSummary';
import containerHasMaterial from '@/lib/containerHasMaterial';
import getPropertyTypeEnum from '@/lib/getPropertyTypeEnum';
function ManyProperties({ allPropertyTypes, propertyTypesCollectingThisMaterial, }) {
    const PROPERTY_TYPE = getPropertyTypeEnum();
    const { postcode } = useParams();
    const tContext = 'material.recycleAtHome.manyProperties';
    const allPropertiesRecycle = propertyTypesCollectingThisMaterial.length === allPropertyTypes.length;
    const sortedPropertyTypes = [...allPropertyTypes]
        .sort((propertyType) => propertyTypesCollectingThisMaterial.includes(propertyType) ? -1 : 1)
        .filter((propertyType) => propertyType !== PROPERTY_TYPE.ALL);
    return (_jsxs(_Fragment, { children: [_jsx("p", { className: "text-size-sm", children: _jsx(Trans, { i18nKey: `${tContext}.collection${allPropertiesRecycle ? 'All' : 'Some'}`, components: { bold: _jsx("strong", {}) } }) }), _jsx("ul", { role: "list", className: "list-style-none diamond-spacing-bottom-md", children: sortedPropertyTypes.map((propertyType) => {
                    const safePropertyType = encodeURIComponent(propertyType);
                    const recyclable = propertyTypesCollectingThisMaterial.includes(propertyType);
                    return (_jsx("li", { className: "diamond-spacing-bottom-sm", children: _jsx("locator-icon-link", { children: _jsxs(Link, { to: `/${postcode}/home/collection?propertyType=${safePropertyType}`, children: [_jsx("locator-icon-circle", { children: _jsx("locator-icon", { icon: recyclable ? 'tick' : 'close', color: recyclable ? 'positive' : 'negative' }) }), propertyType] }) }) }, propertyType));
                }) })] }));
}
function OneProperty({ property, }) {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const containers = property
        .flatMap((scheme) => scheme.containers)
        .filter((container) => containerHasMaterial(container, {
        materials: searchParams.get('materials'),
        category: searchParams.get('category'),
    }));
    return (_jsxs(_Fragment, { children: [_jsx("p", { className: "diamond-text-size-sm", children: t('material.recycleAtHome.oneProperty.collection', {
                    count: containers.length,
                }) }), _jsx(SchemeContainerSummary, { containers: containers })] }));
}
export default function RecycleAtHome({ allProperties, propertiesCollectingThisMaterial, recyclable = true, }) {
    const PROPERTY_TYPE = getPropertyTypeEnum();
    const { postcode } = useParams();
    const { t } = useTranslation();
    const allPropertyTypes = Object.keys(allProperties ?? {});
    const propertyTypesCollectingThisMaterial = Object.keys(propertiesCollectingThisMaterial ?? {});
    let type = 'noProperties';
    if (allPropertyTypes.length === 1 &&
        propertyTypesCollectingThisMaterial.length === 1) {
        type = 'oneProperty';
    }
    if (allPropertyTypes.length > 1 &&
        propertyTypesCollectingThisMaterial.length >= 1) {
        if (allPropertyTypes.includes(PROPERTY_TYPE.ALL) &&
            allPropertyTypes.length === 2) {
            // Display as one property if "All properties" is the only other property type
            type = 'oneProperty';
        }
        else {
            type = 'manyProperties';
        }
    }
    return (_jsxs("diamond-card", { border: true, radius: true, children: [_jsxs("locator-icon-text", { className: "diamond-spacing-bottom-xs", children: [_jsx("locator-icon-circle", { variant: type === 'noProperties' ? 'negative' : 'positive', children: _jsx("locator-icon", { icon: "home" }) }), _jsx("h3", { children: t(`material.recycleAtHome.${type}.${recyclable ? 'title' : 'titleDisposal'}`) })] }), type === 'noProperties' && (_jsx("p", { className: "diamond-text-size-sm", children: t('material.recycleAtHome.noProperties.content') })), type === 'oneProperty' && (_jsx(OneProperty, { property: propertiesCollectingThisMaterial[PROPERTY_TYPE.ALL] ??
                    propertiesCollectingThisMaterial[propertyTypesCollectingThisMaterial[0]] })), type === 'manyProperties' && (_jsx(ManyProperties, { allPropertyTypes: allPropertyTypes, propertyTypesCollectingThisMaterial: propertyTypesCollectingThisMaterial })), _jsx("diamond-button", { width: "full-width", children: _jsx(Link, { to: `/${postcode}/home`, unstable_viewTransition: true, children: t(`material.recycleAtHome.cta`) }) })] }));
}
